<template>
  <div class="bluetooth-print-root" ref="root">
    <!-- 打印按钮 -->
    <el-button 
      :type="buttonType" 
      :size="buttonSize"
      :loading="isConnecting || isPrinting"
      :disabled="disabled"
      @click="handlePrint"
    >
      <i v-if="isConnecting" class="el-icon-loading"></i>
      <i v-else-if="isConnected" class="el-icon-check"></i>
      <i v-else class="el-icon-connection"></i>
      {{ buttonText }}
    </el-button>
    
    <!-- 已连接时显示更换打印机按钮 -->
    <el-button 
      v-if="isConnected && printer.savedDeviceName"
      size="mini"
      type="text"
      @click="handleChangePrinter"
    >
      <i class="el-icon-refresh"></i> 更换
    </el-button>
  </div>
</template>

<script>
import { getPrinter, isBluetoothSupported } from '@/services/bluetooth-printer'
import { getReceivableForPrint } from '@/api/report'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'BluetoothPrintButton',
  props: {
    task: { type: Object, default: null },
    printType: { type: String, default: 'task' },
    orderData: { type: Object, default: null },
    buttonType: { type: String, default: 'primary' },
    buttonSize: { type: String, default: 'small' },
    disabled: { type: Boolean, default: false },
    customContent: { type: String, default: '' }
  },
  data() {
    return {
      printer: null,
      isConnected: false,
      isConnecting: false,
      isPrinting: false,
      showDialog: false,
      dialogState: 'selecting',
      connectingDeviceName: '',
      connectCountdown: 10,
      connectTimer: null,
      connectCancelled: false,
      pairedDevices: [],
      discoveredDevices: [],
      scanning: false,
      selectedDevice: null,
      discoveryListener: null
    }
  },
  computed: {
    buttonText() {
      if (this.isConnecting) return 'Connecting...'
      if (this.isPrinting) return 'Printing...'
      if (this.isConnected) return 'Connected'
      return 'Bluetooth Print'
    },
    dialogTitle() {
      return this.dialogState === 'connecting' ? '正在连接打印机' : '选择蓝牙打印机'
    }
  },
  async created() {
    try {
      this.printer = getPrinter()
      const driverInfo = JSON.parse(localStorage.getItem('driver_info') || '{}')
      const targetUserId = driverInfo.userId || localStorage.getItem('user_id')
      if (targetUserId) {
        this.printer.setUserId(Number(targetUserId))
      }
      
      if (this.printer.isAppEnvironment()) {
        console.log('APP 环境，仅加载设备信息')
        try {
          await this.printer.loadSavedDevice()
        } catch (e) {
          console.warn('加载设备信息失败:', e.message)
        }
        return
      }
      
      await this.printer.loadSavedDevice()
      if (this.printer.savedDeviceId) {
        console.log('已加载蓝牙设备:', this.printer.savedDeviceName)
        if (navigator.bluetooth && navigator.bluetooth.getDevices) {
          try {
            const result = await this.printer.tryAutoConnect()
            if (result.success) {
              this.isConnected = true
              console.log('✅ 自动连接成功:', result.deviceName)
            }
          } catch (error) {
            console.log('自动连接失败，等待用户点击按钮:', error.message)
          }
        }
      }
    } catch (error) {
      console.error('BluetoothPrintButton 初始化失败:', error)
    }
  },
  mounted() {
    // 创建对话框容器并挂到 body 下，避免父容器 transform/filter 导致 position:fixed 失效
    this.portalEl = document.createElement('div')
    this.portalEl.className = 'bt-portal'
    document.body.appendChild(this.portalEl)
    this.renderDialog()
  },
  beforeDestroy() {
    if (this.portalEl && this.portalEl.parentNode) {
      this.portalEl.parentNode.removeChild(this.portalEl)
    }
  },
  watch: {
    showDialog() {
      this.$nextTick(() => this.renderDialog())
    },
    dialogState() {
      this.$nextTick(() => this.renderDialog())
    },
    pairedDevices() {
      this.$nextTick(() => this.renderDialog())
    },
    discoveredDevices() {
      this.$nextTick(() => this.renderDialog())
    },
    scanning() {
      this.$nextTick(() => this.renderDialog())
    },
    connectCountdown() {
      this.$nextTick(() => this.renderDialog())
    },
    connectingDeviceName() {
      this.$nextTick(() => this.renderDialog())
    }
  },
  methods: {
    // 渲染对话框到 body 下的 portal 容器
    renderDialog() {
      if (!this.portalEl) return
      if (!this.showDialog) {
        this.portalEl.innerHTML = ''
        return
      }
      const self = this
      const h = this.$createElement
      // 使用一个临时 Vue 实例来渲染对话框（避免 scoped 问题）
      if (this._dialogVm) {
        this._dialogVm.$destroy()
      }
      const vm = new this.$root.constructor({
        data: { parent: self },
        render(hh) {
          const p = this.parent
          return h('div', { class: ['dialog-overlay'], on: { click: (e) => { if (e.target === e.currentTarget) p.handleMaskClick() } } }, [
            h('div', { class: ['dialog-content'] }, [
              h('div', { class: ['dialog-header'] }, [
                h('h3', p.dialogTitle),
                h('button', { class: ['btn-close'], on: { click: p.closeDialog } }, '×')
              ]),
              h('div', { class: ['dialog-body'] }, p.dialogState === 'connecting' ? [
                h('div', { class: ['connecting-state'] }, [
                  h('i', { class: ['el-icon-loading'] }),
                  h('p', [h('strong', p.connectingDeviceName)]),
                  h('p', { class: ['tip'] }, '请确保打印机已开启且在附近'),
                  h('p', { class: ['tip', 'warning'] }, p.connectCountdown + ' 秒后自动超时...')
                ])
              ] : [
                // 已配对设备
                p.pairedDevices.length > 0 ? h('div', [
                  h('div', { class: ['section-title'] }, '📋 已配对设备'),
                  ...p.pairedDevices.map(d => h('div', { class: ['device-item'], on: { click: () => p.selectDevice(d) } }, [
                    h('i', { class: ['el-icon-printer'] }),
                    h('div', { class: ['device-info'] }, [
                      h('div', { class: ['device-name'] }, [
                        d.name,
                        d.address === p.printer.savedDeviceId ? h('el-tag', { props: { size: 'mini', type: 'warning' } }, '上次连接') : null
                      ]),
                      h('div', { class: ['device-address'] }, d.address)
                    ])
                  ]))
                ]) : null,
                // 扫描到的新设备
                p.discoveredDevices.length > 0 ? h('div', { style: { marginTop: '15px' } }, [
                  h('div', { class: ['section-title'] }, '🔍 扫描到的新设备'),
                  ...p.discoveredDevices.map(d => h('div', { class: ['device-item'], on: { click: () => p.selectDevice(d) } }, [
                    h('i', { class: ['el-icon-discover'] }),
                    h('div', { class: ['device-info'] }, [
                      h('div', { class: ['device-name'] }, d.name),
                      h('div', { class: ['device-address'] }, d.address)
                    ])
                  ]))
                ]) : null,
                // 扫描状态
                p.scanning ? h('div', { class: ['scanning-state'] }, [
                  h('i', { class: ['el-icon-loading'] }),
                  h('p', '正在扫描附近蓝牙设备...')
                ]) : null,
                // 空状态
                !p.scanning && p.pairedDevices.length === 0 && p.discoveredDevices.length === 0 ? h('div', { class: ['empty-state'] }, [
                  h('p', '未找到蓝牙设备'),
                  h('p', { class: ['tip'] }, '请确保打印机已开启且在附近')
                ]) : null
              ].filter(Boolean)),
              h('div', { class: ['dialog-footer'] }, p.dialogState === 'connecting' ? [
                h('button', { class: ['btn-cancel'], on: { click: p.cancelConnect } }, '取消连接'),
                h('button', { class: ['btn-save'], on: { click: p.skipToSelect } }, '选择其他打印机')
              ] : [
                h('button', { class: ['btn-cancel'], on: { click: p.closeDialog } }, '取消'),
                h('button', { class: ['btn-save'], attrs: { disabled: p.scanning }, on: { click: p.startScan } }, [
                  h('i', { class: ['el-icon-refresh'] }),
                  ' 扫描设备'
                ])
              ])
            ])
          ])
        }
      })
      vm.$mount()
      this.portalEl.appendChild(vm.$el)
      this._dialogVm = vm
    },
    async handlePrint() {
      try {
        if (this.isConnected) {
          await this.printContent()
          return
        }

        if (this.printer.isAppEnvironment()) {
          if (this.printer.savedDeviceId) {
            const result = await this.tryAutoConnectWithDialog()
            if (result && result.success) {
              await this.printContent()
              return
            }
            await this.showDeviceSelectionDialog()
            return
          }
          await this.showDeviceSelectionDialog()
          return
        }

        await this.connectPrinter()
      } catch (error) {
        console.error('蓝牙打印错误:', error)
        this.$message.error('蓝牙打印失败: ' + (error.message || '未知错误'))
      }
    },
    
    handleChangePrinter() {
      this.isConnected = false
      this.printer.disconnect()
      this.showDeviceSelectionDialog()
    },

    async tryAutoConnectWithDialog() {
      console.log('APP: 尝试自动连接已保存的设备:', this.printer.savedDeviceName)
      this.isConnecting = true
      this.connectCancelled = false
      this.connectingDeviceName = this.printer.savedDeviceName || '未知设备'
      this.connectCountdown = 10
      this.dialogState = 'connecting'
      this.showDialog = true

      this.connectTimer = setInterval(() => {
        this.connectCountdown--
        if (this.connectCountdown <= 0) {
          clearInterval(this.connectTimer)
        }
      }, 1000)

      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('连接超时，请检查打印机是否开启')), 10000)
        )
        const connectPromise = this.printer.connect()
        
        const result = await Promise.race([connectPromise, timeoutPromise])
        
        clearInterval(this.connectTimer)
        this.showDialog = false
        
        if (this.connectCancelled) {
          return null
        }
        
        if (result.success) {
          this.isConnected = true
          this.$message.success(`已连接: ${result.deviceName}`)
          return result
        } else {
          throw new Error('连接失败')
        }
      } catch (error) {
        clearInterval(this.connectTimer)
        this.showDialog = false
        
        if (this.connectCancelled) {
          return null
        }
        
        console.log('自动连接失败:', error.message)
        this.$message.warning(`连接失败: ${error.message}`)
        return null
      } finally {
        this.isConnecting = false
      }
    },

    cancelConnect() {
      this.connectCancelled = true
      clearInterval(this.connectTimer)
      this.showDialog = false
      this.isConnecting = false
    },

    skipToSelect() {
      this.connectCancelled = true
      clearInterval(this.connectTimer)
      this.isConnecting = false
      this.showDeviceSelectionDialog()
    },

    async showDeviceSelectionDialog() {
      this.connectCancelled = true
      if (this.connectTimer) {
        clearInterval(this.connectTimer)
        this.connectTimer = null
      }
      
      this.dialogState = 'selecting'
      this.showDialog = true
      this.pairedDevices = []
      this.discoveredDevices = []

      try {
        // iOS 使用 BLE 扫描
        if (this.printer.isIOS()) {
          console.log('iOS: 使用 BLE 扫描设备...')
          const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
          if (!BluetoothLe) {
            this.$message.error('BLE 蓝牙插件未安装')
            this.showDialog = false
            return
          }

          // 初始化 BLE（XPC 错误捕获）
          try {
            await BluetoothLe.initialize()
          } catch (initError) {
            console.error('iOS BLE 初始化失败:', initError)
            this.$message.error('蓝牙初始化失败，请重启 APP 后重试')
            this.showDialog = false
            return
          }

          let enabledResult
          try {
            enabledResult = await BluetoothLe.isEnabled()
            console.log('iOS BLE isEnabled 结果:', JSON.stringify(enabledResult))
          } catch (e) {
            console.error('iOS BLE 状态检查失败:', e)
            this.$message.error('无法检查蓝牙状态，请重启 APP 后重试')
            this.showDialog = false
            return
          }
          // 兼容不同的返回格式
          const isBleEnabled = enabledResult.value === true || enabledResult.enabled === true
          console.log('iOS BLE 状态:', isBleEnabled)
          if (!isBleEnabled) {
            this.$message.error('请开启蓝牙')
            this.showDialog = false
            return
          }

          // iOS 请求权限
          try {
            await BluetoothLe.requestPermissions()
          } catch (e) {
            console.warn('iOS 权限请求失败:', e)
          }

          this.startScan()
          return
        }

        // Android 使用经典蓝牙
        if (this.printer.isAndroid()) {
          const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
          if (!ClassicBluetooth) {
            this.$message.error('蓝牙插件未安装')
            this.showDialog = false
            return
          }

          console.log('Android: 请求蓝牙权限...')
          const permResult = await ClassicBluetooth.requestPermissions()
          console.log('权限请求结果:', JSON.stringify(permResult))
          
          const hasPermission = permResult.bluetoothConnect === 'granted' 
            || permResult.bluetoothScan === 'granted' 
            || permResult.location === 'granted'
          
          if (!hasPermission) {
            this.$message.error('需要蓝牙权限才能使用打印功能')
            this.showDialog = false
            return
          }

          const supportResult = await ClassicBluetooth.checkSupport()
          if (!supportResult.supported) {
            this.$message.error('设备不支持蓝牙')
            this.showDialog = false
            return
          }
          if (!supportResult.enabled) {
            this.$message.error('请开启蓝牙')
            this.showDialog = false
            return
          }

          const pairedResult = await ClassicBluetooth.getPairedDevices()
          this.pairedDevices = pairedResult.devices || []

          this.startScan()
          return
        }

        this.$message.error('不支持的平台')
        this.showDialog = false
      } catch (error) {
        console.error('获取蓝牙设备失败:', error)
        this.$message.error('获取蓝牙设备失败: ' + error.message)
      }
    },

    async startScan() {
      this.scanning = true
      this.discoveredDevices = []

      try {
        // iOS BLE 扫描
        if (this.printer.isIOS()) {
          const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
          if (!BluetoothLe) return

          console.log('iOS: 检查 BLE 扫描方法...')
          
          // 检查 startScan 方法是否存在
          if (typeof BluetoothLe.startScan === 'function') {
            console.log('iOS: 开始 BLE 扫描（不过滤 UUID）...')
            await BluetoothLe.startScan({ 
              allowDuplicates: false 
            })

            // BLE 扫描是持续的，通过 listener 接收设备
            this.bleScanListener = BluetoothLe.addListener('onScanResult', (result) => {
              console.log('BLE scan result:', result)
              if (result) {
                const device = {
                  address: result.deviceId || result.address,
                  name: result.name || 'Unknown Device',
                  rssi: result.rssi
                }
                const exists = this.discoveredDevices.some(d => d.address === device.address)
                if (!exists && device.name !== 'Unknown Device') {
                  this.discoveredDevices.push(device)
                }
              }
            })

            // 12 秒后停止扫描
            setTimeout(() => {
              if (this.scanning) {
                this.stopScan()
              }
            }, 12000)
          } else {
            // startScan 不存在，使用 getDevices 获取已配对设备
            console.log('iOS: startScan 不存在，使用 getDevices 获取已配对设备')
            
            if (typeof BluetoothLe.getDevices === 'function') {
              const result = await BluetoothLe.getDevices()
              console.log('iOS: 已配对设备:', result)
              
              const devices = result?.devices || []
              if (devices.length > 0) {
                devices.forEach(d => {
                  if (d.name && d.name !== 'Unknown') {
                    this.discoveredDevices.push({
                      address: d.deviceId || d.address,
                      name: d.name,
                      rssi: d.rssi || 0
                    })
                  }
                })
              } else {
                this.$message.info('未找到已配对的蓝牙设备。请先到 iPhone 设置 → 蓝牙 中配对打印机')
              }
            } else {
              this.$message.error('BLE 插件不支持扫描，请升级到最新版本')
            }
            
            this.scanning = false
          }
          return
        }

        // Android 经典蓝牙扫描
        const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
        if (!ClassicBluetooth) return

        this.discoveryListener = ClassicBluetooth.addListener('deviceFound', (device) => {
          console.log('发现设备:', device)
          const exists = this.discoveredDevices.some(d => d.address === device.address)
          if (!exists) {
            this.discoveredDevices.push(device)
          }
        })

        const finishListener = ClassicBluetooth.addListener('discoveryFinished', () => {
          console.log('扫描完成')
          this.scanning = false
          finishListener.remove()
        })

        await ClassicBluetooth.startDiscovery()
        console.log('开始扫描蓝牙设备...')

        setTimeout(() => {
          if (this.scanning) {
            this.stopScan()
          }
        }, 12000)
      } catch (error) {
        console.error('启动扫描失败:', error)
        this.$message.error('启动扫描失败: ' + error.message)
        this.scanning = false
      }
    },

    async stopScan() {
      try {
        // iOS BLE 停止扫描
        if (this.printer.isIOS()) {
          const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
          if (BluetoothLe) {
            await BluetoothLe.stopScan()
          }
          if (this.bleScanListener) {
            this.bleScanListener.remove()
            this.bleScanListener = null
          }
        }
        
        // Android 经典蓝牙停止扫描
        const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
        if (ClassicBluetooth) {
          await ClassicBluetooth.stopDiscovery()
        }
        if (this.discoveryListener) {
          this.discoveryListener.remove()
          this.discoveryListener = null
        }
      } catch (error) {
        console.error('停止扫描失败:', error)
      } finally {
        this.scanning = false
      }
    },

    async selectDevice(device) {
      this.selectedDevice = device
      this.showDialog = false
      this.isConnecting = true

      try {
        // iOS BLE 连接
        if (this.printer.isIOS()) {
          const BluetoothLe = window.Capacitor?.Plugins?.BluetoothLe
          if (!BluetoothLe) {
            throw new Error('BLE 蓝牙插件未安装')
          }

          console.log('iOS BLE: 连接设备:', device.name, device.address)
          await BluetoothLe.connect({ address: device.address })
          
          // 等待连接建立
          await new Promise(resolve => setTimeout(resolve, 1500))
          
          // ⭐ 设置 MTU（IZM 打印机需要，参考微信小程序）
          try {
            await BluetoothLe.setMTU({ address: device.address, mtu: 100 })
            console.log('BLE: MTU 设置为 100')
          } catch (mtuErr) {
            console.warn('BLE: 设置 MTU 失败（非致命）:', mtuErr.message)
          }
          
          this.isConnected = true
          this.$message.success(`已连接: ${device.name}`)
          await this.printer.saveDeviceToDatabase(device.address, device.name)
          
          // 更新 printer 服务的连接状态
          this.printer.nativeDevice = { deviceId: device.address, name: device.name }
          this.printer.isNativeMode = true
          this.printer.isConnected = true
          
          // ⭐ 连接后立即发现服务和写入特征（参考 IZM 微信小程序）
          try {
            await this.printer.discoverBLECharacteristics()
            console.log('BLE: 写入特征已发现')
          } catch (discErr) {
            console.warn('BLE: 发现特征失败（打印时再试）:', discErr.message)
          }
          
          await this.printContent()
          return
        }

        // Android 经典蓝牙连接
        const ClassicBluetooth = window.Capacitor?.Plugins?.ClassicBluetooth
        if (!ClassicBluetooth) {
          throw new Error('蓝牙插件未安装')
        }

        console.log('Android: 连接设备:', device.name, device.address)
        const result = await ClassicBluetooth.connect({ address: device.address })
        
        if (result.success) {
          this.isConnected = true
          this.$message.success(`已连接: ${device.name}`)
          await this.printer.saveDeviceToDatabase(device.address, device.name)
          
          // 更新 printer 服务的连接状态
          this.printer.nativeDevice = { deviceId: device.address, name: device.name }
          this.printer.isNativeMode = true
          this.printer.isConnected = true
          
          await this.printContent()
        } else {
          throw new Error('连接失败')
        }
      } catch (error) {
        console.error('连接失败:', error)
        this.$message.error('连接失败: ' + error.message)
      } finally {
        this.isConnecting = false
      }
    },

    async connectPrinter() {
      this.isConnecting = true

      try {
        const result = await this.printer.connect()
        
        if (result.success) {
          this.isConnected = true
          this.$message.success(`已连接: ${result.deviceName}`)
          await this.printContent()
        } else {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('连接打印机失败:', error)
        this.$message.error(error.message || '连接失败，请重试')
      } finally {
        this.isConnecting = false
      }
    },

    async printContent() {
      this.isPrinting = true

      try {
        let result
        
        if (this.customContent) {
          result = await this.printer.printReceipt(this.customContent)
        } else if (this.printType === 'order' && this.orderData) {
          let receivableData = null
          const customerId = this.orderData.customerId || this.orderData.customer?.id
          const wholesalerId = getWholesalerId()
          if (customerId) {
            try {
              const res = await getReceivableForPrint(customerId, wholesalerId)
              if (res && res.success && res.data) {
                receivableData = res.data
              }
            } catch (e) {
              console.warn('获取未收款汇总失败:', e.message)
            }
          }
          result = await this.printer.printSingaporeOrder(this.orderData, this.orderData.customer, receivableData)
        } else if (this.task) {
          result = await this.printer.printDeliveryTask(this.task)
        } else {
          throw new Error('No print content specified')
        }

        if (result.success) {
          this.$message.success('Print successful')
        } else {
          throw new Error(result.error)
        }
      } catch (error) {
        console.error('打印失败:', error)
        this.$message.error(`Print failed: ${error.message}`)
      } finally {
        this.isPrinting = false
      }
    },

    closeDialog() {
      this.showDialog = false
    },

    handleMaskClick() {
      if (this.dialogState === 'selecting') {
        this.closeDialog()
      }
    }
  }
}
</script>

<style scoped>
.bluetooth-print-root {
  display: inline-block;
  position: relative;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.dialog-content {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-header h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
}

.btn-close:hover {
  background: #e5e7eb;
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.dialog-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.connecting-state {
  text-align: center;
  padding: 20px;
}

.connecting-state .el-icon-loading {
  font-size: 48px;
  color: #409eff;
}

.connecting-state p {
  margin-top: 15px;
  font-size: 16px;
}

.connecting-state .tip {
  color: #909399;
  font-size: 12px;
  margin-top: 10px;
}

.connecting-state .warning {
  color: #E6A23C;
}

.scanning-state {
  text-align: center;
  padding: 15px;
}

.scanning-state .el-icon-loading {
  font-size: 24px;
}

.empty-state {
  text-align: center;
  padding: 20px;
}

.empty-state .tip {
  color: #909399;
  font-size: 12px;
}

.section-title {
  font-weight: bold;
  margin-bottom: 10px;
}

.device-item {
  display: flex;
  align-items: center;
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.device-item:hover {
  background-color: #f5f7fa;
  border-color: #409eff;
}

.device-item i {
  font-size: 24px;
  color: #409eff;
  margin-right: 15px;
}

.device-info {
  flex: 1;
}

.device-name {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 5px;
}

.device-address {
  font-size: 12px;
  color: #909399;
}

.btn-cancel {
  padding: 10px 20px;
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f9fafb;
}

.btn-save {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-save:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
</style>
